"use client"

import React, { createContext, useContext, useEffect, useState } from "react"
import ItemInfos from "@/src/interfaces/catalog/item-infos"
import useSelectWizard, { AnswerFromCustomer } from "@/src/hooks/product/useWizard"
import Item from "@/src/interfaces/catalog/item"
import { AnswerType } from "@/src/components/product/products/wizard/question/answer/answer"
import { PriceParamsProps, useCalculationProduct } from "@/src/hooks/product/useCalculation"
import Items from "@/src/interfaces/catalog/items"
import { useProductValidations } from "@/src/hooks/product/useValidations"

export interface CustomSize extends Items.ItemResponse {
    isGroup?: boolean
    sizesSelected?: Partial<CustomSize>[]
    complementsSelecteds?: ItemInfos.Complement[]
    description: string
    detailsExternalId: number
    externalId: number
    quantity: number
    sizeType: Item.SizeType
    itemId: string
}

export interface SelectAnswerProps {
    itemOption: ItemInfos.ItemOption
    itemGroup: ItemInfos.ItemGroup
    quantity?: number
    isCombo?: boolean
    mustUpdatePrice?: boolean,
    answerType: AnswerType
}

interface CurrentProductContextProps {
    currentProduct: ItemInfos.DetailedResponse | undefined
    productDetails: ItemInfos.DetailedResponse | undefined
    setProductDetails: (product: ItemInfos.DetailedResponse) => void
    sizeDetails: ItemInfos.DetailedResponse | undefined
    setSizeDetails: (product: ItemInfos.DetailedResponse) => void
    setIsLoading: (e: boolean) => void
    isLoading: boolean
    titleSizeProduct: string | undefined
    setTitleSizeProduct: (title: string | undefined) => void
    price: number
    setPrice: (price: number) => void
    answersFromCustomer: AnswerFromCustomer[]
    setAnswersFromCustomer: (answers: AnswerFromCustomer[]) => void
    questionResultError: string | undefined
    setQuestionResultError: (validationQuestionResult: string) => void
    observation: string | undefined
    setObservation: (obs: string) => void
    sizeProduct: Partial<CustomSize> | undefined
    setSizeProduct: (sizeProduct: Partial<CustomSize>) => void
    clearQuestionResultError: () => void
    handleSelectedAnswer: (e: SelectAnswerProps) => void
    canAddItem: boolean | undefined
    setHasSelectedSize: (e: boolean) => void
    hasSelectedSize: boolean | undefined
    amountProducts: number
    setAmountProducts: (e: number) => void
    setHasWizard: (e: boolean) => void
    hasWizard: boolean
}

const defaultValue = {} as CurrentProductContextProps
export const CurrentProductContext = createContext<CurrentProductContextProps>(defaultValue)

interface Props {
    children: React.ReactNode
}

export const CurrentProductProvider: React.FC<Props> = ({ children }) => {
    const { getPrice } = useCalculationProduct()
    const { scrollToNextQuestion } = useProductValidations()

    const [currentProduct, setCurrentProduct] = useState<ItemInfos.DetailedResponse | undefined>(undefined)
    const [productDetails, setProductDetails] = useState<ItemInfos.DetailedResponse | undefined>(undefined)
    const [sizeDetails, setSizeDetails] = useState<ItemInfos.DetailedResponse | undefined>(undefined)
    const [sizeProduct, setSizeProduct] = useState<Partial<CustomSize> | undefined>(undefined)
    const [observation, setObservation] = useState<string | undefined>()
    const [answersFromCustomer, setAnswersFromCustomer] = useState<AnswerFromCustomer[]>([])
    const [questionResultError, setQuestionResultError] = useState<string | undefined>()
    const [titleSizeProduct, setTitleSizeProduct] = useState<string | undefined>()
    const [price, setPrice] = useState<number>(0)
    const [amountProducts, setAmountProducts] = useState<number>(1)
    const [canAddItem, setCanAddItem] = useState<boolean | undefined>(false)
    const [hasSelectedSize, setHasSelectedSize] = useState<boolean | undefined>(false)
    const [hasWizard, setHasWizard] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const clearQuestionResultError = () => {
        setQuestionResultError(undefined)
        setObservation(undefined)
    }

    const handleSelectedAnswer = (e: SelectAnswerProps) => {
        const useWizard = useSelectWizard()

        const currentAnswerList = useWizard.getAnswersFromCustomer({
            answersFromCustomer: answersFromCustomer,
            answerSelected: e.itemOption,
            group: e.itemGroup,
            index: e.itemGroup.sortIndex,
            isCombo: e.isCombo,
            quantity: e.quantity,
            answerType: e.answerType
        })

        setAnswersFromCustomer(currentAnswerList)

        if (!e.isCombo || e.mustUpdatePrice) {
            const params: PriceParamsProps = {
                currentAnswer: currentAnswerList,
                currentProduct: productDetails,
                sizeProduct: sizeProduct,
                currentPrice: price
            }

            setPrice(getPrice(params))
        }

        const subtitle = document.getElementById(`sub-${e.itemGroup.id}`)

        if (subtitle) {
            subtitle.removeAttribute('style')
            subtitle.textContent = subtitle.textContent?.replace('*', '') ?? ''
        }

        scrollToNextQuestion(e.itemGroup.id, currentAnswerList)
    }

    useEffect(() => {
        const currentProduct = sizeDetails ?? productDetails

        if (currentProduct) {
            setPrice(currentProduct.salePrice)
            setCurrentProduct(currentProduct)
        }
    }, [productDetails, sizeDetails])

    useEffect(() => {
        const currentProduct = sizeDetails ?? productDetails

        if (!currentProduct) return

        const isSizeProduct = (currentProduct && currentProduct.sizes.length > 0) || currentProduct.sizeId
        const isSimpleProduct = (currentProduct && currentProduct.itemGroups.length === 0)
        const isProductWithComplementOrWizard = (currentProduct && currentProduct.itemGroups.length > 0)

        if ((isSizeProduct && !hasSelectedSize) || isLoading) {
            setCanAddItem(false)
            return
        }

        if (isSizeProduct && hasSelectedSize && price >= 0)
            setCanAddItem(true)
        else if (isSimpleProduct && price >= 0)
            setCanAddItem(true)
        else if (isProductWithComplementOrWizard && price >= 0) {
            setCanAddItem(true)

            const isWizardProduct = currentProduct.itemGroups.some(group => group.groupingType === 'Question')
            if (isWizardProduct)
                setHasWizard(true)
        }
        else
            setCanAddItem(false)
    }, [productDetails, price, hasSelectedSize, sizeDetails, isLoading])

    const value = {
        currentProduct,
        productDetails,
        setProductDetails,
        sizeDetails,
        setSizeDetails,
        setIsLoading,
        isLoading,
        titleSizeProduct,
        setTitleSizeProduct,
        price,
        setPrice,
        answersFromCustomer,
        setAnswersFromCustomer,
        questionResultError,
        setQuestionResultError,
        observation,
        setObservation,
        sizeProduct,
        setSizeProduct,
        clearQuestionResultError,
        handleSelectedAnswer,
        canAddItem,
        setHasSelectedSize,
        hasSelectedSize,
        setAmountProducts,
        amountProducts,
        setHasWizard,
        hasWizard
    }

    return (
        <CurrentProductContext.Provider value={value}>
            {children}
        </CurrentProductContext.Provider>
    )
}

export const useCurrentProduct = (): CurrentProductContextProps => {
    const context = useContext(CurrentProductContext)

    if (!context)
        throw new Error("O contexto de CurrentProduct deve estar dentro do CurrentProductProvider.")

    return context
}